// The end of the work (Figma "Untitled" 81:8949): one last screen after the
// last case. "Wow, you've scrolled all the way. Here is a little something": on
// desktop the gift (a photo) shows while the pointer's over its words (a hit
// box larger than them), following the pointer, leaning with its movement; on
// phones a tap shows it, another tap anywhere puts it away. Beside it (phones:
// under it) a pill: Get in touch (Tiago, 5 Nov: About me left out).
import * as React from "react"
import { createPortal } from "react-dom"
import type { Case } from "./types"
import { openContact } from "../shell/Contact"

/** the end's own colours (as a case's: the background, chrome and bars take them) */
export const END: Case = {
    name: "End",
    theme: {
        page: "#1f1f1f",
        gradient: "linear-gradient(180deg, #1f1f1f 0%, #020206 74.629%)",
        stops: [
            [31, 31, 31, 0],
            [2, 2, 6, 0.746],
            [2, 2, 6, 1],
        ],
        fill: "#1f1f1f",
        line: "rgba(255, 255, 255, 0.3)",
        tone: "dark",
        title: "#ffffff",
        body: "#ffffff",
    },
    modules: [],
}

const PHOTO = { src: "/work/end/gift-1200.webp", srcSet: "/work/end/gift-600.webp 600w, /work/end/gift-1200.webp 1200w, /work/end/gift-1800.webp 1800w", w: 4032, h: 3024 }

/** word by word (the entrance brings them in one after the other, as the About's award text) */
const words = (t: string) =>
    t.split(" ").map((w, k) => (
        <React.Fragment key={k}>
            {k > 0 && " "}
            <span className="end-w" data-w="">
                {w}
            </span>
        </React.Fragment>
    ))

export function EndSection() {
    const photo = React.useRef<HTMLImageElement>(null)
    const [shown, setShown] = React.useState(false)
    const fine = typeof matchMedia !== "undefined" && matchMedia("(hover: hover) and (pointer: fine)").matches

    // desktop: the photo follows the pointer (eased), leaning as it moves
    const target = React.useRef({ x: 0, y: 0 })
    const now = React.useRef({ x: 0, y: 0, r: 0, ready: false })
    React.useEffect(() => {
        if (!shown || !fine) return
        let raf = 0
        const step = () => {
            const n = now.current,
                t = target.current
            const dx = t.x - n.x
            n.x += dx * 0.16
            n.y += (t.y - n.y) * 0.16
            // (the lean: how fast it's going sideways, eased, at most 8°)
            n.r += (Math.max(-8, Math.min(8, dx * 0.08)) - n.r) * 0.12
            const el = photo.current
            if (el) el.style.transform = `translate3d(${n.x.toFixed(1)}px, ${n.y.toFixed(1)}px, 0) translate(-50%, -62%) rotate(${n.r.toFixed(2)}deg)`
            raf = requestAnimationFrame(step)
        }
        raf = requestAnimationFrame(step)
        return () => cancelAnimationFrame(raf)
    }, [shown, fine])
    const follow = (e: React.PointerEvent) => {
        target.current = { x: e.clientX, y: e.clientY }
        if (!now.current.ready) now.current = { x: e.clientX, y: e.clientY, r: 0, ready: true }
    }

    // phones: over the words and the buttons together, centred on them
    const row = React.useRef<HTMLDivElement>(null)
    React.useLayoutEffect(() => {
        if (!shown || fine) return
        const r = row.current?.getBoundingClientRect()
        const el = photo.current
        if (!r || !el) return
        const w = Math.min(innerWidth - 16, Math.max(innerWidth - 30, ((r.height + 48) * PHOTO.w) / PHOTO.h))
        el.style.width = w.toFixed(1) + "px"
        el.style.top = (r.top + r.height / 2).toFixed(1) + "px"
    }, [shown, fine])
    // phones: a tap anywhere puts it away
    React.useEffect(() => {
        if (!shown || fine) return
        const close = () => setShown(false)
        const t = setTimeout(() => addEventListener("pointerdown", close, { once: true }), 0)
        return () => (clearTimeout(t), removeEventListener("pointerdown", close))
    }, [shown, fine])

    return (
        <section className="ws ws-end" data-nto-scene="">
            <div ref={row} className="end-row">
                <div className="end-words" data-block="outline">
                    <div className="end-line" data-text="" data-words="">
                        <p>{words("Wow, you’ve scrolled all the way.")}</p>
                    </div>
                    <div className="end-line" data-text="" data-words="">
                        <p>
                            <button
                                type="button"
                                className="end-gift"
                                onPointerEnter={(e) => fine && (follow(e), (now.current.ready = false), follow(e), setShown(true))}
                                onPointerMove={(e) => fine && follow(e)}
                                onPointerLeave={() => fine && (setShown(false), (now.current.ready = false))}
                                onClick={() => !fine && setShown((v) => !v)}
                            >
                                {words("Here is a little something")}
                            </button>
                        </p>
                    </div>
                </div>
                <div className="end-pill" data-block="outline" data-across="" data-from="left">
                    <a
                        className="end-link"
                        data-in=""
                        href="mailto:tiago@nascimen.to"
                        onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                            e.preventDefault()
                            // desktop: the bar opens out of the button; phones: the menu's own form
                            if (fine) openContact(e.currentTarget)
                            else dispatchEvent(new Event("nto:contact-menu"))
                        }}
                    >
                        <span>Get in touch</span>
                        {/* (the dots inline: on hover each moves along on its own) */}
                        <svg className="end-dots" width={10} height={15} viewBox="0 0 10 15" aria-hidden="true">
                            <circle cx="1.5" cy="1.5" r="1.5" />
                            <circle cx="8.5" cy="7.5" r="1.5" />
                            <circle cx="1.5" cy="13.5" r="1.5" />
                        </svg>
                    </a>
                </div>
            </div>
            {createPortal(<img
                ref={photo}
                className="end-photo"
                data-shown={shown || undefined}
                data-fine={fine || undefined}
                src={PHOTO.src}
                srcSet={PHOTO.srcSet}
                sizes="(min-width: 1280px) 30vw, 84vw"
                width={PHOTO.w}
                height={PHOTO.h}
                alt="Tiago as a child, sitting on a kart trolley at a racing track"
                decoding="async"
            />, document.body)}
        </section>
    )
}
